import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import useErrorHandler from "../../hooks/useErrorHandler";
import useSnackbar from "../../hooks/useSnackbar";
import {
  addDeadlineHoliday,
  createDeadlineRule,
  deleteDeadlineHoliday,
  getDeadlineHolidays,
  getDeadlineRules,
  sweepDeadlinesDryRun,
  updateDeadlineRule,
} from "../../api/deadlines";
import { fmtDate } from "./deadline-dialogs";

const EMPTY_RULE = {
  name: "",
  description: "",
  trigger_event: "MANUAL",
  duration_days: 5,
  day_type: "CALENDAR",
  stage: "",
  owner_role: "REGISTRAR",
  reminder_offsets: "3,1,0",
  escalate_after_days: 1,
  active: true,
};

const TRIGGERS = {
  MANUAL: "Manual",
  CASE_CREATED: "Case created",
  CASE_START_DATE: "Case start date",
  "STAGE:INTIMATION": "When case enters Intimation stage",
  "STAGE:APPOINTMENT": "When case enters Appointment stage",
  "STAGE:PLEADINGS": "When case enters Pleadings stage",
  "STAGE:HEARING": "When case enters Hearing stage",
  "STAGE:AWARD": "When case enters Award stage",
};

function RuleDialog({ open, rule, onClose, onDone }) {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_RULE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      rule
        ? {
            ...EMPTY_RULE,
            ...rule,
            description: rule.description || "",
            stage: rule.stage || "",
            reminder_offsets: (rule.reminder_offsets || []).join(","),
          }
        : EMPTY_RULE
    );
  }, [open, rule?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const offsetsValid = /^\s*-?\d+(\s*,\s*-?\d+)*\s*$/.test(String(form.reminder_offsets)) || !String(form.reminder_offsets).trim();
  const valid = form.name.trim() && form.duration_days !== "" && Number(form.duration_days) >= 0 && offsetsValid;

  const submit = () => {
    const body = {
      ...form,
      name: form.name.trim(),
      duration_days: Number(form.duration_days),
      escalate_after_days: Number(form.escalate_after_days) || 0,
      reminder_offsets: String(form.reminder_offsets)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number),
    };
    delete body.id;
    setBusy(true);
    (rule ? updateDeadlineRule(user.token, rule.id, body) : createDeadlineRule(user.token, body))
      .then(() => {
        snackbar.showSuccess(rule ? "Rule updated" : "Rule created");
        onDone();
      })
      .catch(errorHandler)
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{rule ? "Edit rule" : "New rule"}</DialogTitle>
      <DialogContent>
        <Box className="grid grid-cols-1 min-[600px]:grid-cols-2 gap-4 pt-2">
          <TextField size="small" label="Name" required value={form.name} onChange={set("name")} className="min-[600px]:col-span-2" />
          <TextField
            size="small"
            label="Description"
            multiline
            minRows={2}
            value={form.description}
            onChange={set("description")}
            className="min-[600px]:col-span-2"
          />
          <FormControl size="small">
            <InputLabel id="r-trig">Trigger</InputLabel>
            <Select labelId="r-trig" label="Trigger" value={form.trigger_event} onChange={set("trigger_event")}>
              {Object.entries(TRIGGERS).map(([v, l]) => (
                <MenuItem key={v} value={v}>
                  {l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Stage" value={form.stage} onChange={set("stage")} />
          <TextField size="small" type="number" label="Duration (days)" value={form.duration_days} onChange={set("duration_days")} inputProps={{ min: 0 }} />
          <FormControl size="small">
            <InputLabel id="r-dt">Day type</InputLabel>
            <Select labelId="r-dt" label="Day type" value={form.day_type} onChange={set("day_type")}>
              <MenuItem value="CALENDAR">Calendar days</MenuItem>
              <MenuItem value="BUSINESS">Business days</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="r-role">Owner role</InputLabel>
            <Select labelId="r-role" label="Owner role" value={form.owner_role} onChange={set("owner_role")}>
              <MenuItem value="REGISTRAR">Registrar</MenuItem>
              <MenuItem value="ARBITRATOR">Arbitrator</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="USER">User</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Escalate after (days overdue)"
            value={form.escalate_after_days}
            onChange={set("escalate_after_days")}
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            label="Reminder offsets (days before due)"
            value={form.reminder_offsets}
            onChange={set("reminder_offsets")}
            error={!offsetsValid}
            helperText={offsetsValid ? "Comma separated, e.g. 3,1,0" : "Use numbers separated by commas"}
            className="min-[600px]:col-span-2"
          />
          <FormControlLabel
            control={<Switch checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions className="px-6 pb-4">
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || !valid}>
          {busy ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SectionHeader({ title, children }) {
  return (
    <Box className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <Typography className="font-semibold text-[15px] text-[#0d1b36]">{title}</Typography>
      {children}
    </Box>
  );
}

function RulesSection() {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null closed, {} new, rule edit
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    getDeadlineRules(user.token)
      .then(({ data }) => setRules(data.data))
      .catch((err) => (setRules([]), errorHandler(err)))
      .finally(() => setLoading(false));
  }, [tick]);

  return (
    <Paper variant="outlined" className="p-4 rounded-xl">
      <SectionHeader title="Deadline rules">
        <Button variant="contained" size="small" onClick={() => setEditing({})}>
          New rule
        </Button>
      </SectionHeader>
      {loading ? (
        <Box className="py-10 grid place-items-center">
          <CircularProgress size={26} />
        </Box>
      ) : !rules.length ? (
        <Typography className="py-8 text-center text-[#6b7a90]">No rules defined yet.</Typography>
      ) : (
        <Box className="overflow-x-auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Reminders</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.id} className={r.active ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="font-semibold text-[13px] text-[#0d1b36]">{r.name}</div>
                    <div className="text-[12px] text-[#6b7a90]">{r.stage}</div>
                  </TableCell>
                  <TableCell>{TRIGGERS[r.trigger_event] || r.trigger_event}</TableCell>
                  <TableCell>
                    {r.duration_days} {r.day_type === "BUSINESS" ? "business" : "calendar"}
                  </TableCell>
                  <TableCell>{r.owner_role}</TableCell>
                  <TableCell>
                    {(r.reminder_offsets || []).join(", ") || "none"}
                    <span className="text-[12px] text-[#6b7a90]"> · escalate +{r.escalate_after_days}d</span>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.active ? "Active" : "Inactive"} className={r.active ? "!bg-[#e7f6ee] !text-[#1b6b47]" : "!bg-[#eceff4] !text-[#6b7a90]"} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" aria-label="Edit rule" onClick={() => setEditing(r)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <RuleDialog
        open={!!editing}
        rule={editing && editing.id ? editing : null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          setTick((t) => t + 1);
        }}
      />
    </Paper>
  );
}

function HolidaysSection() {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    getDeadlineHolidays(user.token)
      .then(({ data }) => setHolidays(data.data))
      .catch((err) => (setHolidays([]), errorHandler(err)))
      .finally(() => setLoading(false));
  }, [tick]);

  const add = () =>
    addDeadlineHoliday(user.token, { date, name: name.trim() })
      .then(() => {
        snackbar.showSuccess("Holiday added");
        setDate("");
        setName("");
        setTick((t) => t + 1);
      })
      .catch(errorHandler);

  const remove = (d) =>
    deleteDeadlineHoliday(user.token, String(d).slice(0, 10))
      .then(() => {
        snackbar.showSuccess("Holiday removed");
        setTick((t) => t + 1);
      })
      .catch(errorHandler);

  return (
    <Paper variant="outlined" className="p-4 rounded-xl">
      <SectionHeader title="Holidays (skipped for business-day rules)" />
      <Box className="flex flex-wrap items-start gap-3 mb-3">
        <TextField size="small" type="date" label="Date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} className="min-w-[200px]" />
        <Button variant="contained" size="small" className="!h-10" disabled={!date || !name.trim()} onClick={add}>
          Add holiday
        </Button>
      </Box>
      {loading ? (
        <Box className="py-8 grid place-items-center">
          <CircularProgress size={26} />
        </Box>
      ) : !holidays.length ? (
        <Typography className="py-6 text-center text-[#6b7a90]">No holidays configured.</Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {holidays.map((h) => (
              <TableRow key={h.date}>
                <TableCell className="whitespace-nowrap">{fmtDate(h.date)}</TableCell>
                <TableCell>{h.name}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="Remove holiday" onClick={() => remove(h.date)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

function PreviewSection() {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = () => {
    setLoading(true);
    sweepDeadlinesDryRun(user.token)
      .then(({ data }) => setActions(data.actions || []))
      .catch((err) => (setActions(null), errorHandler(err)))
      .finally(() => setLoading(false));
  };

  return (
    <Paper variant="outlined" className="p-4 rounded-xl">
      <SectionHeader title="Reminder preview">
        <Button variant="outlined" size="small" onClick={run} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : "Preview reminders"}
        </Button>
      </SectionHeader>
      <Alert severity="info" className="mb-3">
        Dry run only. Nothing is sent and no deadline is changed. This lists what the next sweep would send.
      </Alert>
      {actions === null ? null : !actions.length ? (
        <Typography className="py-4 text-center text-[#6b7a90]">No reminders or escalations would be sent right now.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Subject</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map((a, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Chip size="small" label={a.kind === "ESCALATION" ? "Escalation" : "Reminder"} className={a.kind === "ESCALATION" ? "!bg-[#fde8e8] !text-[#a52323]" : "!bg-[#eef3fb] !text-[#0b3474]"} />
                </TableCell>
                <TableCell>{a.role}</TableCell>
                <TableCell>{a.subject}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default function RulesAndHolidays() {
  return (
    <Box className="flex flex-col gap-5 px-4 pb-6">
      <RulesSection />
      <HolidaysSection />
      <PreviewSection />
    </Box>
  );
}
